import type { HallazgoApi } from "../api.ts";
import { esTemporal, CIE10, puentesDe, MAX_GRADO, materialCorona } from "../../../dominio/src/index.ts";

/**
 * Dibujo del odontograma (FDI) con la simbología del formulario 033 del MSP:
 * rojo = patología actual o tratamiento necesario, azul = tratamiento realizado.
 */
/** Abreviaturas de lo que no tiene símbolo propio en el formulario 033 (lo que sí lo tiene se dibuja). */
const ABBR: Record<string, string> = { erupcion: "ER", coronaacero: "CA", mantenedor: "ME", fluor: "FB", pulpotomia: "PT", incrustacion: "INC", carilla: "CV", perno: "PM", implante: "IMP",
  incluido: "INCL", supernumerario: "SUP", quiste: "QST", apicectomia: "APX", traccion: "TRC", frenectomia: "FRN", alveoloplastia: "ALV" };

function caras(n: number) {
  const q = Math.floor(n / 10), sup = [1, 2, 5, 6].includes(q), der = [1, 4, 5, 8].includes(q);
  return { top: sup ? "V" : "L", bottom: sup ? "L" : "V", left: der ? "D" : "M", right: der ? "M" : "D", center: "O" } as const;
}

const S = 34, PASO = 40, MEDIO = 14;
/** Recuadros de recesión y movilidad del formulario 033: alto de cada uno, margen izquierdo para sus rótulos y espacio del número y las etiquetas de cada pieza. */
const CAJA = 13, IZQ = 30, NUM = 30, ENTRE = 10;
const FILAS: { piezas: number[]; temporal: boolean; abajo: boolean }[] = [
  { piezas: [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28], temporal: false, abajo: false },
  { piezas: [55, 54, 53, 52, 51, 61, 62, 63, 64, 65], temporal: true, abajo: false },
  { piezas: [85, 84, 83, 82, 81, 71, 72, 73, 74, 75], temporal: true, abajo: true },
  { piezas: [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38], temporal: false, abajo: true },
];
const ANCHO = IZQ + 16 * PASO + MEDIO;

function xDe(i: number, total: number) {
  const offset = total === 10 ? 3 * PASO : 0;
  return IZQ + offset + i * PASO + (i >= total / 2 ? MEDIO : 0);
}

/** Altura de cada fila: en las superiores los recuadros (recesión y luego movilidad) van arriba del número; en las inferiores, debajo (movilidad y luego recesión). */
function disposicion(filas: typeof FILAS) {
  const pos = new Map<(typeof FILAS)[number], { y: number; rec: number; mov: number }>();
  let c = 4, hayAbajo = false;
  for (const f of filas) {
    if (!f.abajo) {
      const rec = c, mov = c + CAJA;
      c += 2 * CAJA + NUM;
      pos.set(f, { y: c, rec, mov });
      c += S + ENTRE;
    } else {
      if (!hayAbajo && pos.size) c += 12;
      hayAbajo = true;
      const y = c;
      c += S + NUM - 3;
      const mov = c, rec = c + CAJA;
      pos.set(f, { y, rec, mov });
      c += 2 * CAJA + ENTRE;
    }
  }
  return { pos, alto: c };
}

interface Props {
  hallazgos: HallazgoApi[];
  denticion: "temporal" | "mixta" | "permanente";
  seleccion: number | null;
  carasMarcadas?: string[];
  alElegir: (n: number) => void;
  alTocarCara?: (n: number, cara: string) => void;
  /** Cambia la recesión o la movilidad de una pieza (grado 1 a 4; null la quita). Sin esto, los recuadros son de solo lectura. */
  alGradoPeriodonto?: (n: number, tipo: "recesion" | "movilidad", grado: number | null) => void;
  /** Dibuja solo esta pieza, sin número ni etiquetas (para el recuadro de cada diente del formulario 033). */
  solo?: number;
}

export function OdontogramaSVG({ hallazgos, denticion, seleccion, carasMarcadas = [], alElegir, alTocarCara, alGradoPeriodonto, solo }: Props) {
  const totales = hallazgos.filter((h) => h.tipo === "protesis_total" && h.diente === null);
  const conHallazgo = new Set(hallazgos.map((h) => h.diente));
  const mostrarTemporales = denticion !== "permanente" || hallazgos.some((h) => h.diente !== null && esTemporal(h.diente));
  const filas = FILAS.filter((f) => !f.temporal || mostrarTemporales);
  const { pos, alto } = disposicion(filas);
  const yAjustada = (f: (typeof FILAS)[number]) => pos.get(f)!.y;
  const posicion = (n: number) => {
    const f = filas.find((x) => x.piezas.includes(n));
    return f ? { x: xDe(f.piezas.indexOf(n), f.piezas.length), y: yAjustada(f) } : null;
  };

  function diente(n: number, x: number, y: number, abajo: boolean) {
    const hs = hallazgos.filter((h) => h.diente === n);
    const relleno: Record<string, string> = {};
    const pendiente = (h: HallazgoApi) => h.estado !== "realizado" && !["ausente", "perdida_otra"].includes(h.tipo);
    for (const h of hs) {
      if (h.tipo === "caries") h.superficies.forEach((c) => (relleno[c] = "caries"));
      if (h.tipo === "obturacion" || h.tipo === "incrustacion") h.superficies.forEach((c) => { if (relleno[c] !== "caries") relleno[c] = pendiente(h) ? "ind" : "obt"; });
      if (h.tipo === "carilla" && relleno.V !== "caries") relleno.V = pendiente(h) ? "ind" : "obt";
      if (h.tipo === "diagnostico") h.superficies.forEach((c) => { if (!relleno[c]) relleno[c] = "dx"; });
    }
    const exf = hs.some((h) => h.tipo === "exfoliado");
    const temporal = esTemporal(n);
    const dim = (denticion === "temporal" && !temporal) || (denticion === "permanente" && temporal) ? !conHallazgo.has(n) : false;
    const i = S * 0.3, j = S * 0.7, c = S / 2, fm = caras(n);
    const P: Record<string, string> = {
      top: `${x},${y} ${x + S},${y} ${x + j},${y + i} ${x + i},${y + i}`,
      bottom: `${x + i},${y + j} ${x + j},${y + j} ${x + S},${y + S} ${x},${y + S}`,
      left: `${x},${y} ${x + i},${y + i} ${x + i},${y + j} ${x},${y + S}`,
      right: `${x + S},${y} ${x + S},${y + S} ${x + j},${y + j} ${x + j},${y + i}`,
      center: `${x + i},${y + i} ${x + j},${y + i} ${x + j},${y + j} ${x + i},${y + j}`,
    };
    const color = (h: HallazgoApi) => (pendiente(h) ? "var(--red)" : "var(--blue)");
    const etiquetas: [string, string][] = [];
    for (const h of hs) {
      const a = ABBR[h.tipo] ?? (h.tipo === "corona" ? materialCorona(h.nota).abrev : null) ?? (h.tipo === "diagnostico" ? (h.cie10 ?? "Dx") : null);
      if (a && !etiquetas.some((e) => e[0] === a)) etiquetas.push([a, color(h)]);
    }
    const yNum = abajo ? y + S + 13 : y - 5;
    const yTag = abajo ? y + S + 25 : y - 17;
    const sel = seleccion === n;
    return (
      <g key={n} className={`t${sel ? " sel" : ""}${dim ? " dim" : ""}${exf ? " exf" : ""}`} role="button" tabIndex={0}
        aria-label={`Pieza ${n}${temporal ? " temporal" : ""}${hs.length ? `, ${hs.length} hallazgos` : ""}`}
        onClick={() => alElegir(n)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); alElegir(n); } }}>
        {solo === undefined && <rect x={x - 3} y={Math.min(y, yNum - 11, yTag - 10) - 2} width={S + 6} height={S + 34} fill="transparent" />}
        {sel && <rect className="selbox" x={x - 3} y={y - 3} width={S + 6} height={S + 6} />}
        {(Object.keys(P) as (keyof typeof fm)[]).map((k) => {
          const cara = fm[k];
          const marcada = sel && carasMarcadas.includes(cara) ? " picked" : "";
          return (
            <polygon key={k} className={`face ${exf ? "" : (relleno[cara] ?? "")}${marcada}`} points={P[k]}
              onClick={alTocarCara ? (e) => { e.stopPropagation(); alElegir(n); alTocarCara(n, cara); } : undefined}>
              <title>{cara}</title>
            </polygon>
          );
        })}
        {hs.map((h) => {
          const col = color(h);
          if (h.tipo === "ausente" || h.tipo === "extraccion")
            return <path key={h.id} d={`M${x - 2} ${y - 2}L${x + S + 2} ${y + S + 2}M${x + S + 2} ${y - 2}L${x - 2} ${y + S + 2}`} stroke={col} strokeWidth={2.6} strokeLinecap="round" pointerEvents="none" />;
          if (h.tipo === "corona" || h.tipo === "coronaacero")
            return <circle key={h.id} cx={x + c} cy={y + c} r={c + 3} fill="none" stroke={col} strokeWidth={2.2} strokeDasharray={h.tipo === "coronaacero" ? "4 2" : undefined} pointerEvents="none" />;
          if (h.tipo === "perdida_otra")
            return <g key={h.id} pointerEvents="none"><circle cx={x + c} cy={y + c} r={c + 2} fill="none" stroke={col} strokeWidth={2.2} /><path d={`M${x + 5} ${y + 5}L${x + S - 5} ${y + S - 5}M${x + S - 5} ${y + 5}L${x + 5} ${y + S - 5}`} stroke={col} strokeWidth={2.4} strokeLinecap="round" /></g>;
          // endodoncia: triángulo (033)
          if (h.tipo === "endodoncia" || h.tipo === "pulpectomia")
            return <path key={h.id} d={`M${x + c} ${y + 5}L${x + S - 6} ${y + S - 6}H${x + 6}Z`} fill="none" stroke={col} strokeWidth={2.4} strokeLinejoin="round" pointerEvents="none" />;
          // sellante: asterisco (033)
          if (h.tipo === "sellante")
            return <path key={h.id} d={`M${x + c} ${y + c - 9}V${y + c + 9}M${x + c - 8} ${y + c - 4.5}L${x + c + 8} ${y + c + 4.5}M${x + c - 8} ${y + c + 4.5}L${x + c + 8} ${y + c - 4.5}`} stroke={col} strokeWidth={2.6} strokeLinecap="round" pointerEvents="none" />;
          return null;
        })}
        {totales.filter((t) => (t.nota === "Superior") === !abajo && !temporal).map((t) => {
          // prótesis total: ═ sobre toda la arcada
          const col = t.estado === "realizado" ? "var(--blue)" : "var(--red)", yy = abajo ? y + S - 3 : y + 3;
          return <path key={t.id} d={`M${x - 3} ${yy - 2.5}H${x + S + 3}M${x - 3} ${yy + 2.5}H${x + S + 3}`} stroke={col} strokeWidth={2} pointerEvents="none" />;
        })}
        {hs.map((h) => {
          const col = color(h);
          if (h.tipo === "pulpotomia") return <circle key={h.id} cx={x + c} cy={y + c} r={S * 0.14} fill={col} pointerEvents="none" />;
          if (h.tipo === "fractura") { const k = S / 40; return <path key={h.id} d={`M${x + 6 * k} ${y + 36 * k}L${x + 17 * k} ${y + 16 * k}L${x + 23 * k} ${y + 26 * k}L${x + 34 * k} ${y + 4 * k}`} fill="none" stroke={col} strokeWidth={2.4} strokeLinejoin="round" pointerEvents="none" />; }
          return null;
        })}
        {solo === undefined && etiquetas.length > 0 && (
          <text className="tagtxt" x={x + c} y={yTag}>
            {etiquetas.slice(0, 2).map((e, k) => <tspan key={e[0]} fill={e[1]}>{e[0]}{k === 0 && etiquetas.length > 1 ? " " : ""}</tspan>)}
            {etiquetas.length > 2 && <tspan fill="var(--muted)"> +{etiquetas.length - 2}</tspan>}
          </text>
        )}
        {solo === undefined && <text className="num" x={x + c} y={yNum}>{n}</text>}
      </g>
    );
  }

  if (solo !== undefined) return <svg className="odo-solo" viewBox={`${-5} ${-5} ${S + 10} ${S + 10}`} role="img" aria-label={`Pieza ${solo}`}>{diente(solo, 0, 0, false)}</svg>;

  return (
    <svg className="odo" viewBox={`0 0 ${ANCHO + 8} ${alto}`} role="group" aria-label="Odontograma">
      <line x1={IZQ + 8 * PASO + MEDIO / 2 - 3} x2={IZQ + 8 * PASO + MEDIO / 2 - 3} y1={8} y2={alto - 8} stroke="var(--line)" strokeDasharray="3 3" />
      {filas.map((f) => f.piezas.map((n, i) => diente(n, xDe(i, f.piezas.length), yAjustada(f), f.abajo)))}
      {filas.map((f) => {
        const p = pos.get(f)!;
        // arriba: recesión y movilidad; abajo: movilidad y recesión (como en el formulario 033)
        const filasCajas: ["recesion" | "movilidad", number][] = f.abajo ? [["movilidad", p.mov], ["recesion", p.rec]] : [["recesion", p.rec], ["movilidad", p.mov]];
        return (
          <g key={`cajas-${f.piezas[0]}`} className="cajas-periodonto">
            {filasCajas.map(([tipo, yy]) => (
              <g key={tipo}>
                <text className="caja-rotulo" x={2} y={yy + CAJA - 3}>{tipo === "recesion" ? "Rec." : "Mov."}</text>
                {f.piezas.map((n, i) => {
                  const x = xDe(i, f.piezas.length);
                  const grado = hallazgos.find((h) => h.diente === n && h.tipo === tipo)?.grado ?? null;
                  const siguiente = grado === null ? 1 : grado >= MAX_GRADO[tipo] ? null : grado + 1;
                  const nombre = tipo === "recesion" ? "Recesión" : "Movilidad";
                  const escala = tipo === "recesion" ? "clase de Miller" : "grado";
                  const editable = !!alGradoPeriodonto;
                  const cambiar = () => alGradoPeriodonto?.(n, tipo, siguiente);
                  return (
                    <g key={n} className={`caja-p${editable ? " edit" : ""}${grado ? " con" : ""}`} role={editable ? "button" : undefined} tabIndex={editable ? 0 : undefined}
                      aria-label={`${nombre} de la pieza ${n}: ${grado ? `${escala} ${grado}` : "sin registrar"}${editable ? ". Toca para cambiar" : ""}`}
                      onClick={editable ? cambiar : undefined} onKeyDown={editable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); cambiar(); } } : undefined}>
                      <rect x={x} y={yy} width={S} height={CAJA - 1} rx={2} />
                      {grado !== null && <text x={x + S / 2} y={yy + CAJA - 3.5} textAnchor="middle">{grado}</text>}
                      <title>{`${nombre} · pieza ${n}${grado ? ` · ${escala} ${grado}` : ""}`}</title>
                    </g>
                  );
                })}
              </g>
            ))}
          </g>
        );
      })}
      {puentesDe(hallazgos).map((g) => {
        // prótesis fija (033): recuadro alrededor de cada pilar, unidos por una línea continua de pilar a pilar
        const a = posicion(g.pilares[0]), b = posicion(g.pilares[1]);
        if (!a || !b) return null;
        const col = g.estado === "realizado" ? "var(--blue)" : "var(--red)", m = 4, yc = a.y + S / 2;
        return (
          <g key={g.pilares.join("-")} className="puente" pointerEvents="none" aria-label={`Prótesis fija ${[...g.pilares].sort((x, y) => x - y).join(" a ")}`}>
            <rect x={a.x - m} y={a.y - m} width={S + 2 * m} height={S + 2 * m} rx={2} fill="none" stroke={col} strokeWidth={2.4} />
            <rect x={b.x - m} y={b.y - m} width={S + 2 * m} height={S + 2 * m} rx={2} fill="none" stroke={col} strokeWidth={2.4} />
            <path d={`M${a.x + S + m} ${yc}H${b.x - m}`} stroke={col} strokeWidth={2.4} />
          </g>
        );
      })}
      {puentesDe(hallazgos, "removible").map((g) => {
        // prótesis removible (033): paréntesis a cada lado de cada pilar, unidos por una línea entrecortada de pilar a pilar
        const a = posicion(g.pilares[0]), b = posicion(g.pilares[1]);
        if (!a || !b) return null;
        const col = g.estado === "realizado" ? "var(--blue)" : "var(--red)", m = 5, yc = a.y + S / 2;
        const parentesis = (x: number, y: number) => (
          <path d={`M${x - 1} ${y - m}Q${x - m - 3} ${y + S / 2} ${x - 1} ${y + S + m}M${x + S + 1} ${y - m}Q${x + S + m + 3} ${y + S / 2} ${x + S + 1} ${y + S + m}`} />
        );
        return (
          <g key={g.pilares.join("-")} className="removible" pointerEvents="none" fill="none" stroke={col} strokeWidth={2.4} strokeLinecap="round" aria-label={`Prótesis removible ${[...g.pilares].sort((x, y) => x - y).join(" a ")}`}>
            {parentesis(a.x, a.y)}
            {g.pilares[0] !== g.pilares[1] && parentesis(b.x, b.y)}
            {g.pilares[0] !== g.pilares[1] && <path d={`M${a.x + S + m + 2} ${yc}H${b.x - m - 2}`} strokeDasharray="6 4" strokeLinecap="butt" />}
          </g>
        );
      })}
    </svg>
  );
}

export function describirHallazgo(h: { tipo: string; superficies: string[]; cie10: string | null; estado: string; grado?: number | null; nota?: string }, nombres: Record<string, string>) {
  const partes = [nombres[h.tipo] ?? h.tipo];
  if (h.grado) partes.push(h.tipo === "recesion" ? `Miller ${h.grado}` : `grado ${h.grado}`);
  if (h.tipo === "protesis_total" && h.nota) partes.push(h.nota);
  if (h.superficies.length) partes.push(h.superficies.join(""));
  if (h.cie10) partes.push(`${h.cie10} ${CIE10[h.cie10] ?? ""}`.trim());
  return partes.join(" · ");
}
